import { format } from 'date-fns';

import { MAX_ASSETS, MAX_LIABILITIES } from '@/constants/finances';
import { getTenantConfigs } from '@/constants/tenant-configs';
import { getFundingSourceOptions, PROFESSION_TEXT } from '@/constants/user';

import i18n from '@/i18n/i18n';
import { getTranslatedMessage } from '@/i18n/utils';

import { CaymanAccountTypeName } from '@/types/api/account';
import { PEPAnswer, PEPQuestion } from '@/types/api/pep';
import { AppConfigurations } from '@/types/app-configurations';
import { Tenant } from '@/types/common';
import { Option } from '@/types/components/common';
import {
  AML4IdentificationType,
  Document,
  DocumentSection,
  DocumentStatus,
  DocumentType,
  SigningDocument,
  SigningFormType,
} from '@/types/document';
import { FetchedFinancialInfo } from '@/types/finance';
import { Language } from '@/types/i18n';
import { CountryISO, Territory } from '@/types/places';
import {
  EmploymentDetails,
  EmploymentStatus,
  FundingSource,
  Gender,
  IncomeSource,
  MaritalStatus,
  Profession,
  User,
  UserAccountType,
} from '@/types/user';

import { getMailingAddressFromUser } from './address';
import { i18nCountryTranslator } from './countryTranslations';
import {
  formatDateValue,
  getAddressLineFromDetailed,
  getDisplayedAddress,
  getDisplayedPhone,
  getFullName,
} from './string';
import {
  getAppointmentBranchName,
  getCitizenships,
  getDisplayCountryName,
  getMonthlyIncome,
  getOccupationName,
  getPEPAnswer,
  getPEPDate,
  getPEPDetails,
  getPlaceOfBirth,
  getProfessionName,
  getUserTitle,
  hasUSFactor,
  isBVIUser,
  isGuyanaUser,
  isNotUSAndLocalTaxResidence,
  isRemediationCustomer,
  isTrinidadOrTobagoUser,
  isUSCitizen,
  isUSTaxResidence,
  parseTaxResidences,
} from './user';

const TAX_REFERENCE_NOT_APPLICABLE = 'N/A';

export const getAccountTypesDataDOA = (accountTypes: UserAccountType[]) => {
  const data: Record<string, string | number | undefined> = {};
  accountTypes.forEach((item, index) => {
    data[`acc_type_${index + 1}`] =
      item.name === CaymanAccountTypeName.EZBanking ? 'EZ' : item.name;
    data[`acc_currency_${index + 1}`] = item.currency;
    data[`monthly_deposit_${index + 1}`] = item.estimatedMonthlyDepositCount;
    data[`total_amount_${index + 1}`] = item.estimatedMonthlyDepositAmount;
    data[`monthly_cash_${index + 1}`] = item.estimatedMonthlyCashDepositAmount;
    data[`acc_send_others_${index + 1}`] = item.willSendReceiveFromOthers
      ? 'yes'
      : 'no';
    data[`acc_send_international_${index + 1}`] =
      item.willUseInternationalWireTransfers ? 'yes' : 'no';
  });
  return data;
};

export function getDocumentTypeName(
  docType: DocumentType | SigningFormType | undefined,
  language: Language,
): string {
  if (!docType) return '';

  return i18n.t(`documentType.${docType}`, {
    lng: language,
    defaultValue: docType,
  });
}

export function getDocumentSigningCharacterFields(
  fieldPrefix: string,
  value?: string,
) {
  if (!value) return {};

  const characters = value.split('');
  const fieldObj: { [key: string]: string } = {};

  characters.forEach((char, index) => {
    fieldObj[`${fieldPrefix}${index + 1}`] = char;
  });

  return fieldObj;
}

function getNonUsTaxResidenceCountries(user: User): string[] {
  const fromTaxResidences = parseTaxResidences(user.taxResidences)
    .map(code => code.toUpperCase())
    .filter(code => code !== CountryISO.US);

  if (fromTaxResidences.length) {
    return fromTaxResidences;
  }

  return Object.keys(user.tin || {})
    .map(code => code.toUpperCase())
    .filter(code => code !== CountryISO.US);
}

export function getDocumentSigningTinParams(user?: User) {
  if (!user) {
    return {};
  }

  const language = (user.language as Language) || Language.English;
  let tinObj: Record<string, string> = {};

  if (user.tin?.[CountryISO.US]) {
    tinObj = {
      ...getDocumentSigningCharacterFields('usTin', user.tin[CountryISO.US]),
    };
  }

  const nonUsCountries = getNonUsTaxResidenceCountries(user);

  nonUsCountries.forEach((countryCode, index) => {
    const countryName =
      i18nCountryTranslator.getCountryName(countryCode, language) || '';
    const tinValue = user.tin?.[countryCode];
    const isCaymanWithoutTin =
      countryCode === CountryISO.Cayman && !tinValue?.trim();

    if (isCaymanWithoutTin) {
      tinObj[`taxResidency${index + 1}`] = countryName;
      tinObj[`taxType${index + 1}`] = TAX_REFERENCE_NOT_APPLICABLE;
      tinObj[`tin${index + 1}`] = TAX_REFERENCE_NOT_APPLICABLE;
      return;
    }

    if (tinValue) {
      tinObj[`taxResidency${index + 1}`] = countryName;
      tinObj[`tin${index + 1}`] = tinValue;
    }
  });

  return tinObj;
}

type RightStartSigningContext = {
  adult?: User;
  minorHolderEmail?: string;
};

// #region AML6
type GetAML6PrefillDataByTenantParams = {
  user: User;
  prefilledData: Record<string, string>;
  language: Language;
  configs: AppConfigurations;
};
const getAML6PrefillDataByTenant = ({
  user,
  prefilledData,
  language,
  configs,
}: GetAML6PrefillDataByTenantParams): Record<string, string> => {
  const mailingFromAddresses = getMailingAddressFromUser(user);
  const mailingAddressPrefill: Record<string, string> = mailingFromAddresses
    ? {
        mailing_address: (mailingFromAddresses?.line1 || '').toUpperCase(),
        mailing_city: (mailingFromAddresses?.city || '').toUpperCase(),
        mailing_state: (mailingFromAddresses?.state || '').toUpperCase(),
        mailing_zip: String(mailingFromAddresses?.zip || '').toUpperCase(),
        mailing_country: getDisplayCountryName(
          mailingFromAddresses.country,
        ).toUpperCase(),
      }
    : {};

  const defaultData = {
    ...prefilledData,
    ...mailingAddressPrefill,
    rim: user.rim || '',
    name: getFullName(user),
    citizenship: getCitizenships(user, language),
    territory:
      i18nCountryTranslator.getCountryName(user.territoryCode, language) || '',
    hasUSFactor: String(hasUSFactor(user)),
    hasGreenCard: String(!!user.usIndicia),
    hasUsTax: String(isUSTaxResidence(user)),
    hasTaxOther: String(isNotUSAndLocalTaxResidence(user)),
    address: (user?.addressDetailed?.line1 || '').toUpperCase(),
    city: (user?.addressDetailed?.city || '').toUpperCase(),
    state: (user?.addressDetailed?.state || '').toUpperCase(),
    country: getDisplayCountryName(
      user?.addressDetailed?.country,
    ).toUpperCase(),
    zip: String(user?.addressDetailed?.zip || '').toUpperCase(),
    poBox: String(user?.addressDetailed?.poBox || '').toUpperCase(),
    birthday: user.birthday
      ? formatDateValue(user.birthday, configs.dateFormat)
      : '',
    ...getDocumentSigningTinParams(user),
  };
  switch (user.tenant) {
    case Tenant.Guyana:
    case Tenant.Trinidad:
      return {
        ...defaultData,
        countryBirth:
          (user.countryOfBirth &&
            i18nCountryTranslator.getCountryName(
              user.countryOfBirth,
              language,
            )) ||
          '',
      };
    default:
      return {
        ...defaultData,
        country:
          (user.citizenship &&
            i18nCountryTranslator.getCountryName(user.citizenship, language)) ||
          '',
      };
  }
};
// #endregion AML6

// #region AML4
const getOccupation = (
  user: User,
  occupations: Option[],
  directorOccupations: Option[],
  language: Language,
  employmentDetail?: EmploymentDetails,
) => {
  const { employmentStatus, employmentDesc, occupationCode } =
    employmentDetail || {};
  let occupation = '';
  switch (employmentStatus) {
    case EmploymentStatus.SelfEmployed:
    case EmploymentStatus.Unemployed:
      occupation =
        getProfessionName(
          PROFESSION_TEXT[employmentDesc as Profession],
          language,
        ) ||
        employmentDesc ||
        '';
      break;
    case EmploymentStatus.Employed:
      if (occupationCode) {
        occupation = getOccupationName({
          user,
          occupations,
          directorOccupations,
        });
      }
      break;
    default:
      break;
  }
  return occupation;
};

type GetMAL4IdentityParamsParams = {
  user: User;
  idDocument: Document | undefined;
  language: Language;
  identificationType?: string;
  expiryDate: string;
};
const getMAL4IdentityParams = ({
  user,
  idDocument,
  language,
  identificationType,
  expiryDate,
}: GetMAL4IdentityParamsParams) => {
  const identityParams = {
    identification_type: identificationType ?? 'other',
    nid_no:
      idDocument?.docType === DocumentType.Id
        ? (idDocument?.docNumber ?? '')
        : '',
    nid_country:
      idDocument?.docType === DocumentType.Id
        ? getDisplayCountryName(user.citizenship, language)
        : '',
    nid_expiry_date: idDocument?.docType === DocumentType.Id ? expiryDate : '',
    p_no:
      idDocument?.docType === DocumentType.Passport
        ? (idDocument?.docNumber ?? '')
        : '',
    p_country:
      idDocument?.docType === DocumentType.Passport
        ? getDisplayCountryName(user.citizenship, language)
        : '',
    p_expiry_date:
      idDocument?.docType === DocumentType.Passport ? expiryDate : '',
    d_no:
      idDocument?.docType === DocumentType.DriverLicense ||
      idDocument?.docType === DocumentType.IdentityDriverLicense
        ? (idDocument?.docNumber ?? '')
        : '',
    d_country:
      idDocument?.docType === DocumentType.DriverLicense ||
      idDocument?.docType === DocumentType.IdentityDriverLicense
        ? getDisplayCountryName(user.citizenship, language)
        : '',
    d_expiry_date:
      idDocument?.docType === DocumentType.DriverLicense ||
      idDocument?.docType === DocumentType.IdentityDriverLicense
        ? expiryDate
        : '',
    other_id_no: identificationType ? '' : (idDocument?.docNumber ?? ''),
    other_id_country: identificationType
      ? ''
      : getDisplayCountryName(idDocument?.issueCountry, language),
    other_id_expiry_date: identificationType ? '' : expiryDate,
  };
  return identityParams;
};

type GetAML4PEPParamsParams = {
  pepConnectionAnswer: PEPAnswer | undefined;
  pepRelativesAnswer: PEPAnswer | undefined;
  language: Language;
  configs: AppConfigurations;
};
const getAML4PEPParams = ({
  pepConnectionAnswer,
  pepRelativesAnswer,
  language,
  configs,
}: GetAML4PEPParamsParams) => {
  const pepParams = {
    pep_connection: pepConnectionAnswer?.positive ? 'yes' : 'no',
    pep_connection_position: pepConnectionAnswer?.position || '',
    pep_connection_country: getDisplayCountryName(
      pepConnectionAnswer?.countryCode,
      language,
    ),
    pep_connection_date: getPEPDate(
      pepConnectionAnswer?.startDate,
      pepConnectionAnswer?.endDate,
      configs.dateFormat,
    ),
    pep_relatives: pepRelativesAnswer?.positive ? 'yes' : 'no',
    pep_relatives_name: pepRelativesAnswer?.fullName || '',
    pep_relatives_position: pepRelativesAnswer?.position || '',
    pep_relatives_relationship: pepRelativesAnswer?.relationship || '',
    pep_relatives_country: getDisplayCountryName(
      pepRelativesAnswer?.countryCode,
      language,
    ),
    pep_relatives_date: getPEPDate(
      pepRelativesAnswer?.startDate,
      pepRelativesAnswer?.endDate,
      configs.dateFormat,
    ),
    pep_details: getPEPDetails(
      pepConnectionAnswer,
      pepRelativesAnswer,
      configs.dateFormat,
    ),
  };
  return pepParams;
};

type GetAML4PrefillData = {
  user: User;
  prefilledData: Record<string, string>;
  language: Language;
  configs: AppConfigurations;
  idDocument: Document | undefined;
  pepConnectionAnswer: PEPAnswer | undefined;
  pepRelativesAnswer: PEPAnswer | undefined;
  occupations: Option[];
  directorOccupations: Option[];
  identificationType?: string;
};

type GetAML4_06_2025PrefillData = GetAML4PrefillData & {
  rightStartData?: RightStartSigningContext;
};

const getAML4PrefillData = ({
  user,
  prefilledData,
  language,
  configs,
  idDocument,
  pepConnectionAnswer,
  pepRelativesAnswer,
  occupations,
  directorOccupations,
  identificationType,
}: GetAML4PrefillData) => {
  let occupation = getOccupation(
    user,
    occupations,
    directorOccupations,
    language,
    user.employmentDetailed,
  );

  const expiryDate = idDocument?.expiryDate
    ? formatDateValue(idDocument.expiryDate, configs.dateFormat)
    : '';

  const defaultData = {
    ...prefilledData,
    branch: user.branchName,
    rim: user.rim || '',
    first_name: user.firstName ?? '',
    last_name: user.lastName ?? '',
    middle_name: user.middleName ?? '',
    address: getDisplayedAddress(user),
    phone_number: getDisplayedPhone(user.phoneNumber),
    email: user.email || '',
    birthday: user.birthday
      ? formatDateValue(user.birthday, configs.dateFormat)
      : '',
    citizenship: getCitizenships(user, language),
    citizenship2: user.citizenship2
      ? (i18nCountryTranslator.getNationalityName(
          user.citizenship2,
          language,
        ) ?? '')
      : '',
    countryBirth: getDisplayCountryName(user.countryOfBirth, language),
    residence: getDisplayCountryName(
      user?.residenceCountry || user?.addressDetailed?.country,
      language,
    ),
    occupation,
    gender: user.gender === Gender.Male ? 'male' : 'female',
    maritalStatus: user.maritalStatus ?? '',
    employed:
      user.employmentDetailed?.employmentStatus &&
      [EmploymentStatus.Employed, EmploymentStatus.SelfEmployed].includes(
        user.employmentDetailed?.employmentStatus,
      )
        ? 'yes'
        : 'no',
    retired:
      user.employmentDetailed?.employmentStatus ===
        EmploymentStatus.Unemployed &&
      user.employmentDetailed?.employmentDesc === Profession.Pensioner
        ? 'yes'
        : 'no',
    employer_name: user.employmentDetailed?.employerName || '',
    employer_address: user.employmentDetailed?.employerAddress || '',
    monthly_deposit: getMonthlyIncome(user),
    hasUSFactor: String(hasUSFactor(user)),
    hasGreenCard: String(!!user.usIndicia),
    hasUsTax: String(isUSTaxResidence(user)),
    hasTaxOther: String(isNotUSAndLocalTaxResidence(user)),
    ...getMAL4IdentityParams({
      user,
      idDocument,
      language,
      identificationType,
      expiryDate,
    }),
    ...getAML4PEPParams({
      pepConnectionAnswer,
      pepRelativesAnswer,
      language,
      configs,
    }),
    ...getDocumentSigningTinParams(user),
  };
  return defaultData;
};

const getAML4APrefillDataIncludingRS = ({
  user,
  prefilledData,
  language,
  configs,
  idDocument,
  pepConnectionAnswer,
  pepRelativesAnswer,
  occupations,
  directorOccupations,
  rightStartData,
}: GetAML4_06_2025PrefillData) => {
  const identificationType = idDocument?.docType
    ? AML4IdentificationType[idDocument.docType]
    : undefined;
  const mailingAddress = getMailingAddressFromUser(user);
  const formData = {
    ...getAML4PrefillData({
      user,
      prefilledData,
      language,
      configs,
      idDocument,
      pepConnectionAnswer,
      pepRelativesAnswer,
      occupations,
      directorOccupations,
      identificationType,
    }),
    mailing_address: mailingAddress
      ? getDisplayedAddress({ ...user, addressDetailed: mailingAddress })
      : '',
  };

  if (!rightStartData?.adult) {
    return formData;
  }

  const adult = rightStartData.adult;
  return {
    ...formData,
    phone_number: getDisplayedPhone(user.phoneNumber || adult.phoneNumber),
    email: user.email || rightStartData.minorHolderEmail || adult.email || '',
    maritalStatus: MaritalStatus.Single,
    employed: 'no',
  };
};

const getAML4A_06_2025PrefillData = (params: GetAML4_06_2025PrefillData) => {
  const adult = params.rightStartData?.adult;
  return {
    ...getAML4APrefillDataIncludingRS(params),
    monthly_deposit: adult
      ? getMonthlyIncome(adult, true)
      : getMonthlyIncome(params.user, true),
  };
};

// #endregion AML4

// #region Authorization
type GetAuthorizationPrefillDataParams = {
  user: User;
  docToSign: SigningDocument;
  configs: AppConfigurations;
  idDocument: Document | undefined;
  rightStartData?: RightStartSigningContext;
  language: Language;
};

const getAuthorizationPrefillData = ({
  user,
  docToSign,
  configs,
  idDocument,
  rightStartData,
  language,
}: GetAuthorizationPrefillDataParams) => {
  const userIdDocument = user?.documents
    .filter(doc => doc.status !== DocumentStatus.Rejected)
    .find(doc => doc.type === DocumentSection.Identity);

  let prefilledData: Record<string, string> = {
    ...docToSign.prefillData,
    branch: user?.branchName ?? '',
    date: format(new Date(), configs.dateFormat),
    name: getFullName(user) ?? '',
    first_name: user?.firstName ?? '',
    last_name: user?.lastName ?? '',
    id_type: getDocumentTypeName(userIdDocument?.docType, language),
    id_no: userIdDocument?.docNumber ?? '',
  };

  if (rightStartData) {
    const adultIdDocument = rightStartData?.adult?.documents
      .filter(doc => doc.status !== DocumentStatus.Rejected)
      .find(doc => doc.type === DocumentSection.Identity);
    prefilledData = {
      ...prefilledData,
      ...docToSign.prefillData,
      is_rightstart: 'true',
      name_rs_minor: getFullName(user),
      first_name_rs_minor: user.firstName ?? '',
      last_name_rs_minor: user.lastName ?? '',
      id_type_rs_minor: getDocumentTypeName(idDocument?.docType, language),
      id_no_rs_minor: idDocument?.docNumber ?? '',
      email_rs_minor: rightStartData.minorHolderEmail || user.email || '',
      rim: rightStartData.adult?.rim || '',
      rim_rs_minor: user.rim || '',
      is_minor_required: user.allowToDebitCardOnlineBanking ? 'true' : 'false',
      is_minor_allow_to_manage: user.allowToManage ? 'true' : 'false',
      branch: rightStartData?.adult?.branchName ?? '',
      date: format(new Date(), configs.dateFormat),
      name: getFullName(rightStartData?.adult) ?? '',
      first_name: rightStartData?.adult?.firstName ?? '',
      last_name: rightStartData?.adult?.lastName ?? '',
      id_type: getDocumentTypeName(adultIdDocument?.docType, language),
      id_no: adultIdDocument?.docNumber ?? '',
    };
  }
  return prefilledData;
};
// #endregion Authorization

// #region CA43A
type GetCA43APrefillDataParams = {
  user: User;
  docToSign: SigningDocument;
  configs: AppConfigurations;
  idDocument: Document | undefined;
  rightStartData?: RightStartSigningContext;
  language: Language;
};

const getCA43APrefillData = ({
  user,
  docToSign,
  configs,
  idDocument,
  rightStartData,
}: GetCA43APrefillDataParams) => {
  let prefilledData: Record<string, string> = {
    ...docToSign.prefillData,
    date: format(new Date(), configs.dateFormat),
    name: getFullName(user),
    id_no: idDocument?.docNumber || '',
    first_name: user.firstName || '',
    last_name: user.lastName || '',
    rim: user.rim || '',
  };

  if (rightStartData) {
    const adultIdDocument = rightStartData.adult?.documents
      .filter(doc => doc.status !== DocumentStatus.Rejected)
      .find(doc => doc.type === DocumentSection.Identity);

    prefilledData = {
      ...prefilledData,
      name: getFullName(rightStartData.adult) || getFullName(user),
      id_no: adultIdDocument?.docNumber || '',
      first_name: rightStartData.adult?.firstName ?? user.firstName ?? '',
      last_name: rightStartData.adult?.lastName ?? user.lastName ?? '',
      rim: rightStartData.adult?.rim || '',
      is_rightstart: 'true',
      name_rs_minor: getFullName(user),
      id_no_rs_minor: idDocument?.docNumber || '',
      first_name_rs_minor: user.firstName || '',
      last_name_rs_minor: user.lastName || '',
      rim_rs_minor: user.rim || '',
      email_rs_minor: rightStartData.minorHolderEmail || user.email || '',
      is_minor_required: user.allowToSign ? 'true' : 'false',
    };
    if (isGuyanaUser(user) || isBVIUser(user)) {
      prefilledData.is_minor_required = user.allowToDebitCardOnlineBanking
        ? 'true'
        : 'false';
    }
  }

  return prefilledData;
};
// #endregion CA43A

// #region FV511
type GetFV511PrefillDataParams = {
  user: User;
  prefilledData: Record<string, string>;
  configs: AppConfigurations;
  rightStartData?: RightStartSigningContext;
  language: Language;
};

const getFV511Name = (user?: User) =>
  [user?.firstName, user?.lastName, user?.middleName].filter(Boolean).join(' ');

const getFV511Location = (
  user: User | undefined,
  language: Language,
  includeAddress: boolean,
) => {
  const address = user?.addressDetailed;
  if (!address) return '';

  const stateAndZip = [address.state, address.zip].filter(Boolean).join(' ');
  const country = getDisplayCountryName(address.country, language);

  return [
    includeAddress ? address.line1 : undefined,
    address.city,
    stateAndZip,
    country,
  ]
    .filter(Boolean)
    .join(', ');
};

const getFV511PrefillData = ({
  user,
  prefilledData,
  configs,
  rightStartData,
  language,
}: GetFV511PrefillDataParams): Record<string, string> => {
  const adult = rightStartData?.adult;

  return {
    ...prefilledData,
    date: format(new Date(), configs.dateFormat),
    branch: adult?.branchName ?? '',
    name: getFV511Name(user),
    address: getFV511Location(user, language, true),
    residence: getFV511Location(user, language, false),
    name_rs_adult: getFV511Name(adult),
    address_rs_adult: getFV511Location(adult, language, true),
    residence_rs_adult: getFV511Location(adult, language, false),
  };
};
// #endregion FV511

// #region DOA
type GetDOAPrefillDataParams = {
  user: User;
  configs: AppConfigurations;
  idDocument: Document | undefined;
  prefilledData: Record<string, string>;
  territoryByCountryCode?: Record<string, Territory>;
  occupation: string;
  userLanguage: Language;
};

const getDOAPrefillData = ({
  user,
  configs,
  idDocument,
  prefilledData,
  territoryByCountryCode,
  occupation,
  userLanguage,
}: GetDOAPrefillDataParams) => {
  const formData = {
    ...prefilledData,
    title: user?.title ? getUserTitle(Number(user.title)) : '',
    full_name: getFullName(user).toUpperCase(),
    first_name: String(user.firstName).toUpperCase(),
    last_name: String(user.lastName).toUpperCase(),
    middle_name: String(user.middleName).toUpperCase(),
    address: String(user?.addressDetailed?.line1).toUpperCase(),
    po_box: String(user?.addressDetailed?.poBox).toUpperCase(),
    zip: String(user?.addressDetailed?.zip).toUpperCase(),
    country: getDisplayCountryName(
      user?.addressDetailed?.country,
    ).toUpperCase(),
    district: String(user?.addressDetailed?.city).toUpperCase(),
    phone_number: getDisplayedPhone(user.phoneNumber),
    email: user.email || '',
    visit_branch: getAppointmentBranchName(
      user,
      territoryByCountryCode,
    ).toUpperCase(),
    marital_status: user.maritalStatus ?? '',
    citizenship: getCitizenships(user, userLanguage).toUpperCase(),
    country_birth: getDisplayCountryName(
      user.countryOfBirth,
      userLanguage,
    ).toUpperCase(),
    birthday: user.birthday
      ? formatDateValue(user.birthday, configs.dateFormat)
      : '',
    residence: getDisplayCountryName(
      user?.addressDetailed?.country,
      userLanguage,
    ).toUpperCase(),
    employer_name: String(user.employmentDetailed?.employerName).toUpperCase(),
    employer_address: String(
      user.employmentDetailed?.employerAddress,
    ).toUpperCase(),
    employer_phone: user.employmentDetailed?.employerPhone || '',
    employer_position: String(occupation).toUpperCase(),
    employer_start_date: user.employmentDetailed?.startDate
      ? formatDateValue(user.employmentDetailed.startDate, configs.dateFormat)
      : '',
    passport:
      idDocument?.docType === DocumentType.Passport
        ? (idDocument?.docNumber ?? '')
        : '',
    issue_date:
      idDocument?.docType === DocumentType.Passport && idDocument.issueDate
        ? formatDateValue(idDocument.issueDate, configs.dateFormat)
        : '',
    expiry_date:
      idDocument?.docType === DocumentType.Passport && idDocument.expiryDate
        ? formatDateValue(idDocument.expiryDate, configs.dateFormat)
        : '',
    country_passport:
      idDocument?.docType === DocumentType.Passport
        ? getDisplayCountryName(user.citizenship, userLanguage)
        : '',
    total_annual_deposit: user.totalAnnualDeposit || '',
    ...getAccountTypesDataDOA(user.accountTypes || []),
  };
  return formData;
};
// #endregion DOA

type GetSigningFormURLParams = {
  docToSign: SigningDocument;
  occupations: Option[];
  directorOccupations: Option[];
  user?: User;
  userAccountTypes?: UserAccountType[];
  pepQuestions?: PEPQuestion[];
  territoryByCountryCode?: Record<string, Territory>;
  rightStartData?: RightStartSigningContext;
  financialInfo?: FetchedFinancialInfo[];
};

type SigningFormPrefillContext = {
  user: User;
  prefilledData: Record<string, string>;
  configs: AppConfigurations;
  idDocument: Document | undefined;
  rightStartData?: RightStartSigningContext;
  userLanguage: Language;
  occupation: string;
  territoryByCountryCode?: Record<string, Territory>;
  userAccountTypes?: UserAccountType[];
  financialInfo: FetchedFinancialInfo[];
};

const getW9PrefillData = ({
  user,
  prefilledData,
}: SigningFormPrefillContext): Record<string, string> => ({
  ...prefilledData,
  name: getFullName(user, { middleNameAbbreviation: true }),
  address1: user?.addressDetailed?.line1 || '',
  address2: getAddressLineFromDetailed(user?.addressDetailed),
  ...getDocumentSigningCharacterFields('ssn', user.tin?.[CountryISO.US]),
});

const getFatca1APrefillData = ({
  user,
  prefilledData,
}: SigningFormPrefillContext): Record<string, string> => ({
  ...prefilledData,
  name: getFullName(user),
  rim: user.rim || '',
  tin: (hasUSFactor(user) && user.tin?.[CountryISO.US]) || '',
  hasGreenCard: String(!!user.usIndicia),
  hasUsStatus: String(hasUSFactor(user)),
});

const getSignaturePrefillData = ({
  user,
  prefilledData,
  configs,
  idDocument,
  rightStartData,
}: SigningFormPrefillContext): Record<string, string> => ({
  ...prefilledData,
  ...(rightStartData?.adult && user.allowToDebitCardOnlineBanking !== true
    ? {
        branch: rightStartData.adult.branchName,
        name: getFullName(rightStartData.adult),
        dob: rightStartData.adult.birthday
          ? formatDateValue(rightStartData.adult.birthday, configs.dateFormat)
          : '',
        id_no:
          rightStartData.adult.documents
            .filter(doc => doc.status !== DocumentStatus.Rejected)
            .find(doc => doc.type === DocumentSection.Identity)?.docNumber ||
          '',
        rim: rightStartData.adult.rim || '',
      }
    : {
        branch: user.branchName,
        name: getFullName(user),
        dob: user.birthday
          ? formatDateValue(user.birthday, configs.dateFormat)
          : '',
        id_no: idDocument?.docNumber || '',
        rim: user.rim || '',
      }),
  ...(rightStartData && {
    is_rightstart: 'true',
    email_rs_minor: rightStartData.minorHolderEmail || user.email || '',
    is_minor_required: user.allowToDebitCardOnlineBanking ? 'true' : 'false',
  }),
});

const getIDCprefillData = ({
  user,
  prefilledData,
}: SigningFormPrefillContext): Record<string, string> => ({
  ...prefilledData,
  customer_id: user.id.toString(),
  customer_name: getFullName(user),
});

const getISCPrefillData = ({
  user,
  prefilledData,
  configs,
}: SigningFormPrefillContext): Record<string, string> => {
  const isUsCitizenRenouncement = user?.usCitizenshipRenouncement === true;

  return {
    ...prefilledData,
    name: getFullName(user).toUpperCase(),
    birthday: user.birthday
      ? formatDateValue(user.birthday, configs.dateFormat)
      : '',
    placeOfBirth: getPlaceOfBirth(user),
    address: (user?.addressDetailed?.line1 || '').toUpperCase(),
    ...getDocumentSigningCharacterFields('ssn', user.tin?.[CountryISO.US]),
    city: (user?.addressDetailed?.city || '').toUpperCase(),
    state: (user?.addressDetailed?.state || '').toUpperCase(),
    country: getDisplayCountryName(
      user?.addressDetailed?.country,
    ).toUpperCase(),
    zip: String(user?.addressDetailed?.zip || '').toUpperCase(),
    poBox: String(user?.addressDetailed?.poBox || '').toUpperCase(),
    ...(!isUsCitizenRenouncement && {
      hasUSFactor: String(isUSTaxResidence(user) || isUSCitizen(user)),
      usTin:
        isUSTaxResidence(user) || isUSCitizen(user)
          ? user.tin?.[CountryISO.US] || ''
          : '',
    }),
    USRenouncement: String(isUsCitizenRenouncement),
    ...getDocumentSigningTinParams(user),
  };
};

// #region AML10

const getFundingSourceDisplayLabel = (
  sourceType: string,
  tenant: User['tenant'],
  description?: string,
) => {
  if (sourceType === FundingSource.OtherIncome && description) {
    return description;
  }

  return (
    getFundingSourceOptions(tenant).find(option => option.value === sourceType)
      ?.text || sourceType
  );
};

const getAccountTypeDisplayLabel = (account: UserAccountType) =>
  account.type || account.name || '';

const shouldUseRemediationSourceOfFundFormat = (user?: User) => {
  if (!isTrinidadOrTobagoUser(user)) {
    return false;
  }

  if (isRemediationCustomer(user)) {
    return true;
  }

  const hasAccounts = (user?.accountTypes?.length ?? 0) > 0;
  const hasIncomeSources = (user?.incomeSources?.length ?? 0) > 0;

  return (
    user?.employmentDetailed?.requireBasicInfo === false &&
    hasAccounts &&
    hasIncomeSources
  );
};

const getIncomeSourceDisplayLabel = (
  incomeSource: NonNullable<User['incomeSources']>[number],
  user: User,
) =>
  getFundingSourceDisplayLabel(
    incomeSource.sourceType,
    user.tenant,
    incomeSource.description,
  );

const getRemediationAML10SourceOfFundValue = (
  user: User,
  userAccountTypes?: UserAccountType[],
) => {
  if (!user) return '';

  const accounts = userAccountTypes?.length
    ? userAccountTypes
    : user.accountTypes || [];
  const incomeSources = user.incomeSources || [];

  const shouldPairAccountWithIncomeByIndex =
    incomeSources.length === accounts.length;

  return incomeSources
    .map((incomeSource, index) => {
      const account = shouldPairAccountWithIncomeByIndex
        ? accounts[index]
        : accounts[0];
      const accountTypeLabel = getAccountTypeDisplayLabel(account);
      const incomeSourceLabel = getIncomeSourceDisplayLabel(incomeSource, user);

      return `(${accountTypeLabel}) ${incomeSourceLabel}`;
    })
    .join(', ');
};

const getAML10PrefillData = ({
  user,
  prefilledData,
  financialInfo,
  userAccountTypes,
}: SigningFormPrefillContext): Record<string, string> => {
  const assets = financialInfo.filter(item => item.financialType === 'ASSET');
  const liabilities = user.hasLiability
    ? financialInfo.filter(item => item.financialType === 'LIABILITY')
    : [];

  const totalAssets = assets.reduce((sum, item) => sum + item.amount, 0);
  const totalLiabilities = liabilities.reduce(
    (sum, item) => sum + item.amount,
    0,
  );

  const assetFields: Record<string, string> = {};
  assets.slice(0, MAX_ASSETS).forEach((item, index) => {
    const n = index + 1;
    assetFields[`asset_${n}`] = item.financialSubtype;
    assetFields[`asset_${n}_value`] = String(item.amount);
  });

  const liabilityFields: Record<string, string> = {};
  liabilities.slice(0, MAX_LIABILITIES).forEach((item, index) => {
    const n = index + 1;
    liabilityFields[`liability_${n}`] = item.financialSubtype;
    liabilityFields[`liability_${n}_value`] = String(item.amount);
  });

  const sourceOfFund = getSourceOfFundDisplay(user, userAccountTypes);

  return {
    ...prefilledData,
    rim: user.rim || '',
    first_name: user.firstName || '',
    middle_name: user.middleName || '',
    last_name: user.lastName || '',
    email: user.email || '',
    ...assetFields,
    ...liabilityFields,
    total_assets: String(totalAssets),
    total_liabilities: String(totalLiabilities),
    total_monthly_income: String(user.totalMonthlyIncome ?? ''),
    total_monthly_expense: String(user.totalMonthlySpending ?? ''),
    source_of_fund: sourceOfFund,
    frequency: user.howOftenPaid?.toUpperCase() ?? '',
    other_frequency: user.howOftenPaidOther ?? '',
  };
};

function getSourceOfFundDisplay(
  user: User,
  userAccountTypes?: UserAccountType[],
): string {
  if (!user) return '';

  const accounts = userAccountTypes?.length
    ? userAccountTypes
    : user.accountTypes || [];
  const incomeSources = user.incomeSources || [];

  const shouldUseRemediationFormat =
    shouldUseRemediationSourceOfFundFormat(user) &&
    accounts.length > 0 &&
    incomeSources.length > 0;

  if (shouldUseRemediationFormat) {
    return getRemediationAML10SourceOfFundValue(user, userAccountTypes);
  }

  if (incomeSources.length) {
    return incomeSources
      .map(incomeSource => getIncomeSourceDisplayLabel(incomeSource, user))
      .join(', ');
  }

  const { sourceOfIncome, sourceOfIncomeOther } = user;
  if (sourceOfIncome) {
    try {
      const sources: string[] = JSON.parse(sourceOfIncome);
      return sources
        .map(source => {
          if (source === IncomeSource.Other) {
            return sourceOfIncomeOther || '';
          }
          return getTranslatedMessage(
            `moreInfo.form.yourFinances.income.source.${source}`,
          );
        })
        .filter(Boolean)
        .join(', ');
    } catch {
      return '';
    }
  }

  if (user.fundingSource) {
    return getFundingSourceDisplayLabel(user.fundingSource, user.tenant);
  }

  return '';
}

// #endregion AML10

/** `user` is the signatory profile; with Right Start / operation flow, pass the minor as `user` and adult + holder email in `rightStartData`. */
export function getSigningFormURL({
  docToSign,
  occupations,
  directorOccupations,
  user,
  userAccountTypes,
  pepQuestions,
  territoryByCountryCode,
  rightStartData,
  financialInfo,
}: GetSigningFormURLParams) {
  if (!user) return docToSign.url;
  const USER_LANGUAGE = (user.language as Language) || Language.English;
  const configs = getTenantConfigs(user.tenant);
  const idDocument = user.documents
    .filter(doc => doc.status !== DocumentStatus.Rejected)
    .find(doc => doc.type === DocumentSection.Identity);

  const occupation = getOccupationName({
    user,
    occupations,
    directorOccupations,
  });

  let prefilledData: Record<string, string> = {
    ...docToSign.prefillData,
    client_id: user.id.toString(),
  };
  const signingFormPrefillContext: SigningFormPrefillContext = {
    user,
    prefilledData,
    configs,
    idDocument,
    userLanguage: USER_LANGUAGE,
    occupation,
    territoryByCountryCode,
    userAccountTypes,
    financialInfo: financialInfo || [],
  };

  switch (docToSign.docType) {
    case SigningFormType.Authorization: {
      const authorizationPrefilledData = getAuthorizationPrefillData({
        user,
        docToSign,
        configs,
        idDocument,
        rightStartData,
        language: USER_LANGUAGE,
      });
      prefilledData = {
        ...prefilledData,
        ...authorizationPrefilledData,
      };
      break;
    }
    case SigningFormType.AML4A: {
      const { pepConnectionAnswer, pepRelativesAnswer } = getPEPAnswer(
        user,
        pepQuestions || [],
      );

      prefilledData = getAML4APrefillDataIncludingRS({
        user,
        prefilledData,
        language: USER_LANGUAGE,
        configs,
        idDocument,
        pepConnectionAnswer,
        pepRelativesAnswer,
        occupations,
        directorOccupations,
        rightStartData,
      });
      break;
    }
    case SigningFormType.AML4A_04_2025:
    case SigningFormType.AML4A_06_2025: {
      const { pepConnectionAnswer, pepRelativesAnswer } = getPEPAnswer(
        user,
        pepQuestions || [],
      );

      prefilledData = getAML4A_06_2025PrefillData({
        user,
        prefilledData,
        language: USER_LANGUAGE,
        configs,
        idDocument,
        pepConnectionAnswer,
        pepRelativesAnswer,
        occupations,
        directorOccupations,
        rightStartData,
      });
      break;
    }
    case SigningFormType.AML6:
      prefilledData = getAML6PrefillDataByTenant({
        user,
        prefilledData,
        language: USER_LANGUAGE,
        configs,
      });
      break;
    case SigningFormType.CA43A: {
      const ca43APrefilledData = getCA43APrefillData({
        user,
        docToSign,
        configs,
        idDocument,
        rightStartData,
        language: USER_LANGUAGE,
      });
      prefilledData = {
        ...prefilledData,
        ...ca43APrefilledData,
      };
      break;
    }
    case SigningFormType.FV511:
      prefilledData = getFV511PrefillData({
        user,
        prefilledData,
        configs,
        rightStartData,
        language: USER_LANGUAGE,
      });
      break;
    case SigningFormType.W9:
      prefilledData = getW9PrefillData({
        ...signingFormPrefillContext,
        prefilledData,
      });
      break;
    case SigningFormType.FATCA_1A:
      prefilledData = getFatca1APrefillData({
        ...signingFormPrefillContext,
        prefilledData,
      });
      break;
    case SigningFormType.SIGNATURE:
      prefilledData = getSignaturePrefillData({
        ...signingFormPrefillContext,
        prefilledData,
        rightStartData,
      });
      break;
    case SigningFormType.IDC:
      prefilledData = getIDCprefillData({
        ...signingFormPrefillContext,
        prefilledData,
      });
      break;
    case SigningFormType.ISC:
      prefilledData = getISCPrefillData({
        ...signingFormPrefillContext,
        prefilledData,
      });
      break;
    case SigningFormType.DOA:
      prefilledData = getDOAPrefillData({
        ...signingFormPrefillContext,
        prefilledData,
      });
      break;
    case SigningFormType.AML10:
      prefilledData = getAML10PrefillData({
        ...signingFormPrefillContext,
        prefilledData,
      });
      break;
  }

  return Object.keys(prefilledData).length
    ? docToSign.url + `#${new URLSearchParams(prefilledData).toString()}`
    : docToSign.url;
}
